import React, { FC, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { useChat } from './hooks/useChat'
import { ConversationList } from './components/ConversationList'
import { ChatWindow } from './components/ChatWindow'
import { NewChatModal, ContactSearchResult as GraphQLContactResult } from './components/NewChatModal'
import { SendMessageCanvas } from '../../components/messaging/SendMessageCanvas'
import { ContactSearchResult } from './hooks/useChat'
import { parseId } from '../../helpers'

const InboxPage: FC = () => {
    const {
        conversations,
        currentConversation,
        setCurrentConversation,
        messages,
        loadingInbox,
        loadingMessages,
        sendMessage,
        startConversation,
        assignedOnly,
        setAssignedOnly,
        // Filters
        filterUnread, setFilterUnread,
        filterTags, setFilterTags,
        filterDateFrom, setFilterDateFrom,
        filterDateTo, setFilterDateTo,
        availableTags,
        fetchTags,
        createTag,
        manageTag,
        configSource,
        availableConfigs,
        reloadInbox
    } = useChat()

    const [showNewChat, setShowNewChat] = useState(false)
    const [showSendMessage, setShowSendMessage] = useState(false)
    const [selectedRecipient, setSelectedRecipient] = useState<ContactSearchResult | null>(null)

    // Client-side filters
    const [filterWhatsapp, setFilterWhatsapp] = useState(false)
    const [filterSms, setFilterSms] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    // Filter conversations based on selected channels
    // Logic: If filter is ON, conversation MUST have that channel active.
    // If multiple filters are ON, it's an AND condition (must have BOTH).
    // Note: Since we are now doing server-side filtering for other things, 
    // maybe channel filtering should also be server-side? 
    // For now keep it client side as user didn't request backend changes for it specifically.
    const filteredConversations = conversations.filter(c => {
        const hasWhatsapp = c.active_channels?.includes('WHATSAPP') || c.last_message?.channel === 'WHATSAPP'
        const hasSms = c.active_channels?.includes('SMS') || c.last_message?.channel === 'SMS'

        if (filterWhatsapp && !hasWhatsapp) return false
        if (filterSms && !hasSms) return false
        if (searchQuery && c.full_name && !c.full_name.toLowerCase().includes(searchQuery.toLowerCase())) return false

        return true
    })

    // Map GraphQL result (camelCase) to local format (snake_case)
    const handleContactSelected = (contact: GraphQLContactResult) => {
        const mappedContact: ContactSearchResult = {
            id: Number(parseId(contact.id)), // Decode Relay Global ID (base64) to numeric PK
            full_name: contact.fullName,
            phone: contact.phone,
            email: contact.email
        }
        setSelectedRecipient(mappedContact)
        setShowNewChat(false)
        setShowSendMessage(true)
    }

    const handleMessageSent = () => {
        if (selectedRecipient) {
            startConversation(selectedRecipient)
        }
        setShowSendMessage(false)
    }


    return (
        <>
            <PageTitle breadcrumbs={[]}>Inbox</PageTitle>

            <div className='d-flex flex-column flex-lg-row inbox-page'>
                {/* Sidebar */}
                <div className='flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-10 mb-lg-0'>
                    <ConversationList
                        conversations={filteredConversations}
                        selectedId={currentConversation?.id}
                        onSelect={setCurrentConversation}
                        onNewChat={() => setShowNewChat(true)}
                        onRefresh={reloadInbox}
                        loading={loadingInbox}
                        searchQuery={searchQuery}
                        onSetSearchQuery={setSearchQuery}
                        assignedOnly={assignedOnly}
                        onToggleAssignedOnly={setAssignedOnly}
                        filterWhatsapp={filterWhatsapp}
                        onToggleWhatsapp={setFilterWhatsapp}
                        filterSms={filterSms}
                        onToggleSms={setFilterSms}

                        filterUnread={filterUnread}
                        onToggleUnread={setFilterUnread}
                        filterDateFrom={filterDateFrom}
                        onSetDateFrom={setFilterDateFrom}
                        filterDateTo={filterDateTo}
                        onSetDateTo={setFilterDateTo}
                        filterTags={filterTags}
                        onSetTags={setFilterTags}
                        availableTags={availableTags}
                    />
                </div>

                {/* Content */}
                <div className='flex-lg-row-fluid ms-lg-7 ms-xl-10'>
                    <ChatWindow
                        activeConversation={currentConversation}
                        messages={messages}
                        loading={loadingMessages}
                        onSendMessage={sendMessage}
                        availableTags={availableTags}
                        onCreateTag={createTag}
                        onManageTag={manageTag}
                        configSource={configSource}
                        availableConfigs={availableConfigs}
                    />
                </div>
            </div>

            <NewChatModal
                show={showNewChat}
                onHide={() => setShowNewChat(false)}
                onSelect={handleContactSelected}
            />

            {selectedRecipient && (
                <SendMessageCanvas
                    show={showSendMessage}
                    onHide={() => setShowSendMessage(false)}
                    recipient={{
                        name: selectedRecipient.full_name,
                        phone: selectedRecipient.phone,
                        email: selectedRecipient.email
                    }}
                    context={{
                        type: 'CONTACT',
                        id: selectedRecipient.id
                    }}
                    onSuccess={handleMessageSent}
                />
            )}
        </>
    )
}

export { InboxPage }
